import { useEffect } from 'react';
import { capture } from '../lib/analytics';
import { claimClerkWakeRetry } from '../lib/clerkLoadRetry';
import { elapsedMsSince, monotonicNow } from '../lib/elapsedMs';
import { log } from '../lib/logger';

/** Which surface is showing the Clerk load failure. Rides the analytics event. */
export type ClerkFailureSurface = 'protected_route' | 'sign_in';

/**
 * While a Clerk load failure is on screen, reload once more when the user comes
 * back: the tab becomes visible again, or the browser reports it is online.
 *
 * See the wake-retry note in lib/clerkLoadRetry.ts for the incident and for why
 * this stays bounded. Three properties this hook has to keep:
 *
 * - It listens only while `active` (the failure is actually showing). Armed any
 *   earlier, an ordinary tab switch during a slow load would spend the claim on
 *   a page that was about to recover by itself.
 * - It acts on a TRANSITION, never on the current state. A failure that appears
 *   in a visible, online tab waits; reloading there would just be a second
 *   immediate retry.
 * - The claim, not the listener, is what bounds it. Listeners can fire any
 *   number of times and two failure screens can be mounted at once; only the
 *   first claim reloads.
 *
 * Caller cadence for `auth_load_wake_retried`: at most once per tab, because it
 * is emitted only after a granted claim.
 */
export function useClerkWakeRetry(active: boolean, surface: ClerkFailureSurface): void {
  useEffect(() => {
    if (!active) return;
    const shownAt = monotonicNow();

    const retry = (trigger: 'visible' | 'online') => {
      if (!claimClerkWakeRetry()) return;
      const waitedMs = elapsedMsSince(shownAt);
      log.info('[useClerkWakeRetry] reloading', { surface, trigger, waitedMs });
      // posthog-js flushes on pagehide, so the event survives the reload.
      capture('auth_load_wake_retried', { surface, trigger, waited_ms: waitedMs });
      window.location.reload();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') retry('visible');
    };
    const onOnline = () => retry('online');

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
    };
  }, [active, surface]);
}
